import { FormControl, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { ChangingDirectionType } from "../types";
import { SelectorButtonContent } from "../views";
import { ToolsPanelButton } from "./ToolsPanelButton";

type StepChangeSelectorProps = {
    selectedStep: number;
    setSelectedStep: React.Dispatch<React.SetStateAction<number>>;
    stepOptions: number[];
    disabled: boolean;
    buttonContent: string;
    previousTooltipText: string;
    nextTooltipText: string;
    selectorWidth?: number;
    onChange: (direction: ChangingDirectionType, step: number) => void | Promise<void>;
    onStepSelectionChange?: (step: number) => void | Promise<void>;
};

export function StepChangeSelector({
    selectedStep,
    setSelectedStep,
    stepOptions,
    disabled,
    buttonContent,
    previousTooltipText,
    nextTooltipText,
    selectorWidth = 70,
    onChange,
    onStepSelectionChange,
}: StepChangeSelectorProps) {
    const handleChange = (direction: ChangingDirectionType): void => {
        void onChange(direction, selectedStep);
    };

    const handleStepSelection = (eventArg: SelectChangeEvent<string>): void => {
        const selectedStep = parseFloat(eventArg.target.value);
        setSelectedStep(selectedStep);
        void onStepSelectionChange?.(selectedStep);
    };

    const displayPrecision = Math.max(
        ...stepOptions.map((option) => {
            const [, fraction = ''] = option.toString().split('.');
            return fraction.length;
        })
    );

    const formatStepValue = (value: number): string =>
        displayPrecision > 0
            ? value.toFixed(displayPrecision)
            : value.toString();

    return (
        <Stack direction="row" spacing={0.5}>
            <FormControl variant="standard">
                <Select
                    value={selectedStep.toString()}
                    onChange={handleStepSelection}
                    renderValue={(value) => formatStepValue(parseFloat(value))}
                    sx={{
                        width: selectorWidth,
                        '.MuiFormLabel-root': {
                            fontWeight: 'bold'
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '24px',
                        },
                        '&.Mui-focused .MuiSelect-select': {
                            backgroundColor: 'transparent',
                        },
                        fontSize: '15px',
                        fontWeight: 'bold',
                        height: '24px',
                        margin: '0px 0px 0px 0px',
                        padding: '4px 0px 0px 0px'
                    }}
                    disabled={disabled}
                    MenuProps={{
                        MenuListProps: {
                            sx: {
                                paddingY: 0.25,
                            },
                        },
                        PaperProps: {
                            sx: {
                                width: selectorWidth,
                                maxHeight: '415px',
                            },
                            TransitionProps: {
                                timeout: 0,
                            },
                        },
                    }}
                >
                    {stepOptions.map((option) => (
                        <MenuItem
                            key={option}
                            value={option.toString()}
                            sx={{
                                fontSize: 14,
                                paddingY: 0.25,
                                paddingX: 1,
                                minHeight: 32,
                                justifyContent: 'flex-end',
                                textAlign: 'right',

                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(0,0,0,0.08)',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                }
                            }}
                        >
                            {formatStepValue(option)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ToolsPanelButton
                tooltipText={previousTooltipText}
                disabled={disabled}
                onChanged={() => handleChange(ChangingDirectionType.Previous)}
            >
                <SelectorButtonContent content={buttonContent} direction={ChangingDirectionType.Previous} />
            </ToolsPanelButton>
            <ToolsPanelButton
                tooltipText={nextTooltipText}
                disabled={disabled}
                onChanged={() => handleChange(ChangingDirectionType.Next)}
            >
                <SelectorButtonContent content={buttonContent} direction={ChangingDirectionType.Next} />
            </ToolsPanelButton>
        </Stack>
    );
}
